// The trade-ticket preview, shared by the BUY and SELL tickets (15 Oct 2026).
//
// Root cause it replaces: each ticket fired a new preview on every edit AND every 20 s with no guard for a
// request already in flight, and every newer request discarded the older answer - even one for the same
// inputs. The backend runs one owner's broker calculators one at a time, so overlapping previews queued up
// behind each other; once a queue was longer than the 20 s refresh the ticket discarded every answer and
// showed "updating…" (and the last stale planned pairs) indefinitely.
//
// Now:
//  - one request per INPUT KEY (path + body): an edit waits for typing to pause (debounce), aborts the
//    obsolete request, and asks once; identical inputs are never asked twice in a row
//  - an answer is applied only if it is for the inputs on screen NOW; anything older is dropped
//  - the periodic refresh runs only while idle (never on top of a request in flight)
//  - `stale` = the shown answer is not for the current inputs -> sizing must not be presented as current
//  - errors are kept per key with `retry()`
import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";

export const PREVIEW_DEBOUNCE_MS = 600;
export const PREVIEW_REFRESH_MS = 20000;

export function usePreview({ enabled, path, body, debounceMs = PREVIEW_DEBOUNCE_MS, refreshMs = PREVIEW_REFRESH_MS, post = api.post }) {
  const key = enabled && path ? JSON.stringify([path, body]) : null;
  const [state, setState] = useState({ key: null, data: null, error: null, at: null });
  const [loading, setLoading] = useState(false);
  const ctrl = useRef(null);
  const inflight = useRef(null);
  const latest = useRef(key);
  latest.current = key;

  const run = useCallback(async (k, force = false) => {
    if (!k || (!force && inflight.current === k)) return;
    if (ctrl.current) ctrl.current.abort();
    const c = typeof AbortController !== "undefined" ? new AbortController() : null;
    ctrl.current = c;
    inflight.current = k;
    setLoading(true);
    const [p, b] = JSON.parse(k);
    try {
      const r = await post(p, b, c ? { signal: c.signal } : undefined);
      if (latest.current === k) setState({ key: k, data: r.data, error: null, at: Date.now() });
    } catch (e) {
      if (c?.signal.aborted || e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      if (latest.current === k) setState((s) => ({ key: k, data: s.key === k ? s.data : null, error: apiError(e), at: Date.now() }));
    } finally {
      if (ctrl.current === c) { ctrl.current = null; inflight.current = null; setLoading(false); }
    }
  }, [post]);

  // a new input key: wait for the typing to pause, then ask once (never again for an answered key)
  useEffect(() => {
    if (!key) { if (ctrl.current) ctrl.current.abort(); return undefined; }
    if (state.key === key && !state.error) return undefined;
    const t = setTimeout(() => run(key), debounceMs);
    return () => clearTimeout(t);
  }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps

  // refresh the CURRENT inputs while idle (prices move); never stacks on a request in flight
  useEffect(() => {
    if (!key || !refreshMs) return undefined;
    const i = setInterval(() => { if (!inflight.current && latest.current === key) run(key); }, refreshMs);
    return () => clearInterval(i);
  }, [key, refreshMs, run]);

  useEffect(() => () => { if (ctrl.current) ctrl.current.abort(); }, []);

  const current = state.key === key;
  return {
    data: current ? state.data : null,          // only ever the answer for what is on screen
    lastData: state.data,                       // the previous answer (prices only - never sizing)
    error: current ? state.error : null,
    stale: !!key && !current,
    loading,
    at: current ? state.at : null,
    retry: () => run(key, true),
  };
}

/** Create, with the figures the ticket SHOWED; a 409 PREVIEW_CHANGED returns the server's new figures. */
export async function createWithExpected(body, preview, post = api.post) {
  const expected = preview ? { planned_pairs: preview.planned_pairs ?? null, capital_per_pair: preview.capital_per_pair ?? null } : undefined;
  try {
    const r = await post("/options-hedge/trades", { ...body, expected });
    return { trade: r.data.trade };
  } catch (e) {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 409 && d?.code === "PREVIEW_CHANGED") return { changed: d };
    return { error: apiError(e) };
  }
}
